import React, { useMemo } from 'react';

import TextNode from 'components/shared/Nodes/TextNode';

import { selectZoomedPPI } from 'selectors/ppi';
import { useAppDispatch, useAppSelector } from 'utils/hooks';

import ui from 'domains/ui';
import textNodes from 'domains/text_nodes';

import type { NodeAPIData } from 'types/catalog_types';

type BetterTextNodeContainerProps = {
  node: NodeAPIData;
};

function BetterTextNodeContainer(props: BetterTextNodeContainerProps) {
  const { node } = props;

  const dispatch = useAppDispatch();

  const ppi = useAppSelector(selectZoomedPPI);
  const textData = useAppSelector((state) => textNodes.selectors.selectById(state, node.uid));
  const activeNodeUid = useAppSelector(textNodes.selectors.activeNode);

  const isActive = useMemo(() => node.uid === activeNodeUid, [node, activeNodeUid]);

  const nodeDimension = useMemo(() => {
    const [x, y, w, h] = node.bounds.split(',').map((bound) => parseFloat(bound) * ppi);
    return { x, y, w, h };
  }, [node, ppi]);

  // Style for entire node
  const styles = useMemo(() => {
    const { x, y, w, h } = nodeDimension;
    const rotation = node.rotation || 0;

    // Bump to top of stacking context when we're editing
    const zIndex = isActive ? 9999 : 2000 + node.sort_order;

    return {
      node: {
        zIndex,
        top: `${y}px`,
        left: `${x}px`,
        height: `${h}px`,
        width: `${w}px`,
      },
      svg: {
        height: '100%',
        width: '100%',
        transform: `rotate(${rotation}deg)`,
      },
    };
  }, [nodeDimension, node, isActive]);

  if (!textData) return null;

  return (
    <TextNode
      onClick={() => {
        dispatch(textNodes.actions.setActiveTextNodeUid(node.uid));
        dispatch(ui.actions.setSidebarComponent('TextTools'));
      }}
      showWarning={false}
      node={node}
      nodeDimension={nodeDimension}
      styles={styles}
      fontSize={parseInt(textData.fontSize)}
      fontFamily={textData.fontFamily}
      textColor={textData.textColor}
      textContent={textData.textContent}
      verticalAlign={textData.verticalAlignment}
      horizontalAlign={textData.horizontalAlignment}
      autofit={textData.autofit}
      active={node.uid === activeNodeUid}
    />
  );
}

export default BetterTextNodeContainer;
